import {
  ActionIcon,
  Button,
  Divider,
  Flex,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconCirclePlus, IconUsers, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { StepProps } from 'src/common/ModalFlow';
import ModalBox from 'src/UI/common/ModalBox';
import { Client } from 'src/admin/users/types';
import { NameCell } from 'src/common/components/table/NameCell';
import {
  InProgressFamily,
  InProgressMainMember,
  InProgressSecondaryMember,
  isSameFamilyMember,
  memberId,
} from '../dataHelpers';
import { AddFromContactsView } from './AddFromContactsView';
import { AddManuallyFormValues, AddManuallyView } from './AddManuallyView';
import { formatName } from 'src/utils/nameFormatting';

type Props = StepProps<InProgressFamily>;
type View = 'initial' | 'fromContacts' | 'manual';

export default function AddContactsToFamilyModal({
  close,
  data: family,
  nextStep,
  previousStep,
  canGoBack,
}: Props) {
  const [currentView, setCurrentView] = useState<View>('initial');
  const [mainFamilyMember, setMainFamilyMember] = useState<InProgressMainMember | null>(
    family?.mainFamilyMember || null,
  );
  const [secondaryFamilyMembers, setSecondaryFamilyMembers] = useState<InProgressSecondaryMember[]>(
    family?.secondaryFamilyMembers || [],
  );
  const familyMembers = [mainFamilyMember, ...secondaryFamilyMembers].filter(Boolean) as (
    | InProgressMainMember
    | InProgressSecondaryMember
  )[];

  const addMember = (member: InProgressMainMember | InProgressSecondaryMember) => {
    if (mainFamilyMember == null) {
      setMainFamilyMember(member);
    } else if (isSameFamilyMember(mainFamilyMember, member)) {
      setMainFamilyMember(null);
    } else {
      setSecondaryFamilyMembers(prev => [...prev, member]);
    }
  };

  const removeMember = (member: InProgressMainMember | InProgressSecondaryMember) => {
    if (mainFamilyMember != null && isSameFamilyMember(mainFamilyMember, member)) {
      setMainFamilyMember(null);
    } else {
      setSecondaryFamilyMembers(prev => prev.filter(m => !isSameFamilyMember(m, member)));
    }
  };

  const handleSelectClient = (client: Client) => {
    const isMainMember = mainFamilyMember != null && isSameFamilyMember(mainFamilyMember, client);
    const isSecondaryMember = secondaryFamilyMembers.find(m => isSameFamilyMember(m, client));
    const isAlreadyMember = isMainMember || isSecondaryMember;

    if (isAlreadyMember) {
      removeMember(client);
    } else {
      addMember(client);
    }
  };

  const handleAddManually = (values: AddManuallyFormValues) => {
    addMember(values);
  };

  const toggleContactsView = () => {
    setCurrentView(currentView === 'fromContacts' ? 'initial' : 'fromContacts');
  };

  const toggleManualView = () => {
    setCurrentView(currentView === 'manual' ? 'initial' : 'manual');
  };

  const handleContinue = async () => {
    if (!family?.name || !family?.advisorUserId || !mainFamilyMember) {
      throw new Error('Missing required family data');
    }

    await nextStep({
      name: family.name,
      advisorUserId: family.advisorUserId,
      mainFamilyMember,
      secondaryFamilyMembers,
    });
  };

  const handleBack = () => {
    previousStep({
      ...family,
      mainFamilyMember: mainFamilyMember || undefined,
      secondaryFamilyMembers: secondaryFamilyMembers || undefined,
    });
  };

  const vw = currentView === 'initial' ? 50 : 80;

  const selectedClients: Client[] = familyMembers.filter(member => 'id' in member) as Client[];

  const canContinue =
    familyMembers.length > 1 && mainFamilyMember != null && family?.name && family?.advisorUserId;

  // This looks very complex but unless we change the state data structure
  // this is the best way I could think to avoid with duplicates in the familyMembers list
  const handleChangeMainFamilyMember = (value: string | null) => {
    if (value === null) {
      if (mainFamilyMember !== null) {
        setSecondaryFamilyMembers([...secondaryFamilyMembers, mainFamilyMember]);
      }
      setMainFamilyMember(null);
      return;
    }

    const newMainFamilyMember = familyMembers.find(c => memberId(c) === value);

    if (!newMainFamilyMember) {
      if (mainFamilyMember !== null) {
        setSecondaryFamilyMembers([...secondaryFamilyMembers, mainFamilyMember]);
      }
      setMainFamilyMember(null);
      return;
    }

    const newSecondaryList = secondaryFamilyMembers.filter(
      member => !isSameFamilyMember(member, newMainFamilyMember),
    );

    if (mainFamilyMember !== null) {
      newSecondaryList.push(mainFamilyMember);
    }

    setSecondaryFamilyMembers(newSecondaryList);
    setMainFamilyMember(newMainFamilyMember);
  };

  const isClientInAnotherFamily = (client: Client) => {
    if (!client.familyId) return false;

    return !familyMembers.some(member => 'id' in member && client.id === member.id);
  };

  return (
    <Modal opened onClose={close} withCloseButton={false} centered size="xxl" variant="multi">
      <Flex gap="xxs" h="80vh" mih="80vh" direction="row" w={`${vw}vw`} miw={`${vw}vw`}>
        {currentView === 'fromContacts' && (
          <AddFromContactsView
            selectedClients={selectedClients}
            onSelectClient={handleSelectClient}
            maw="450px"
            flex={4}
            my="sm"
            showModalControls={false}
            isCheckboxDisabled={isClientInAnotherFamily}
          />
        )}
        {currentView === 'manual' && (
          <AddManuallyView
            onSubmit={handleAddManually}
            otherMembersEmails={familyMembers.map(m => m.emailAddress).filter(m => !!m) as string[]}
            maw="450px"
            flex={4}
            my="sm"
            showModalControls={false}
          />
        )}
        <ModalBox
          title={`${family?.name} Family`}
          onClose={close}
          flex={6}
          previousStep={handleBack}
          canGoBack={canGoBack}
        >
          <Flex direction="column" flex={1}>
            <Group mb="xl">
              <Button
                variant={currentView === 'fromContacts' ? 'black' : 'outline-black'}
                onClick={toggleContactsView}
                leftSection={<IconUsers size={16} />}
              >
                Add from contacts
              </Button>
              <Button
                variant={currentView === 'manual' ? 'black' : 'outline-black'}
                onClick={toggleManualView}
                leftSection={<IconCirclePlus size={16} />}
              >
                Add manually
              </Button>
            </Group>
            <Select
              data={familyMembers.map(member => ({
                value: memberId(member),
                label: formatName(member.firstName, member.lastName),
              }))}
              label="Please choose a primary client"
              placeholder="Select a primary client"
              disabled={familyMembers.length === 0}
              required
              withAsterisk
              mb="md"
              value={mainFamilyMember && memberId(mainFamilyMember)}
              onChange={value => handleChangeMainFamilyMember(value)}
            />

            {familyMembers.length === 0 ? (
              <Flex ta="center" align="center" direction="column" justify="center" flex={1}>
                <img
                  src="/images/empty-illustration.png"
                  alt="Family empty"
                  width={158}
                  height={118}
                />
                <Text fw={600} c="gray.10" size="md" mb="xs">
                  Start by adding clients to the {family?.name} family
                </Text>
                <Text fw={400} c="gray.7" size="sm" mb="xl">
                  You can add clients manually, select them from your contacts list or import them
                  using a CSV file.
                </Text>
              </Flex>
            ) : (
              <Stack gap="xs">
                <Text fw={600} c="gray.10" size="md" mb="xs">
                  Family members
                </Text>
                {familyMembers.map((member, i) => [
                  <Flex key={memberId(member)} align="center" justify="space-between">
                    <NameCell entity={member} />
                    <ActionIcon variant="subtle" color="gray" onClick={() => removeMember(member)}>
                      <IconX size={16} />
                    </ActionIcon>
                  </Flex>,
                  i !== familyMembers.length - 1 && <Divider color="gray.1" key={`divider-${i}`} />,
                ])}
              </Stack>
            )}

            <Group mt="auto" justify="flex-end">
              <Button variant="outline-black" onClick={close}>
                Cancel
              </Button>
              <Tooltip
                label={'A family must have at least 2 members and have a primary client assigned'}
                withArrow
                disabled={!!canContinue}
              >
                <Button variant="black" onClick={handleContinue} disabled={!canContinue}>
                  Continue
                </Button>
              </Tooltip>
            </Group>
          </Flex>
        </ModalBox>
      </Flex>
    </Modal>
  );
}
